import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Avatar,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Visibility, 
  AccountBalanceWallet, 
  Add as AddIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CurrencyRupee,
  Group as GroupIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  Casino as CasinoIcon,
  WhatsApp as WhatsAppIcon,
  AccountTree as AccountTreeIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  FlightTakeoff as FlightTakeoffIcon,
  EmojiNature as EmojiNatureIcon,
  Filter7 as Filter7Icon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { userAPI, walletAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openWalletDialog, setOpenWalletDialog] = useState(false);
  const [openTreeDialog, setOpenTreeDialog] = useState(false);
  const [treeStatusLoading, setTreeStatusLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsTabValue, setDetailsTabValue] = useState(0);
  const [treeData, setTreeData] = useState(null);
  const [treeStatistics, setTreeStatistics] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  
  // Pagination states
  const [bettingPage, setBettingPage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const bettingPageSize = 5;
  const transactionPageSize = 5;
  
  // Form states
  const [createUserType, setCreateUserType] = useState('agent');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    new_user_type: 'agent',
    wallet_limit: '',
    initial_balance: 0,
  });
  
  // Wallet update form
  const [walletFormData, setWalletFormData] = useState({
    amount: '',
    operation: 'add',
    notes: '',
  });

  // Filter state
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.listUsers();
      setUsers(response.data.users);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Handle user creation
  const handleCreateUser = async () => {
    try {
      const endpoint = formData.new_user_type === 'users' ? 'createPlayerUser' : 'createUser';
      const response = await userAPI[endpoint](formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setOpenCreateDialog(false);
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  // Handle user details view
  const handleViewDetails = async (userId) => {
    try {
      const response = await userAPI.getUserDetails(userId);
      if (response.data.success) {
        setSelectedUser({
          ...response.data.user,
          betting_history: response.data.betting_history,
          transaction_history: response.data.transaction_history
        });
        setDetailsTabValue(0); // Reset to first tab
        // Reset pagination when opening new user details
        setBettingPage(1);
        setTransactionPage(1);
        setOpenDetailsDialog(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch user details');
    }
  };

  // Handle view user tree
  const handleViewTree = async (userId) => {
    try {
      setTreeLoading(true);
      setOpenTreeDialog(true);
      const response = await userAPI.getUserTree(userId);
      if (response.data.success) {
        setTreeData(response.data.tree);
        setTreeStatistics(response.data.statistics || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch user tree');
      setOpenTreeDialog(false);
    } finally {
      setTreeLoading(false);
    }
  };

  // Handle toggle tree status (activate/deactivate entire tree)
  const handleToggleTreeStatus = async (userId, action) => {
    const actionText = action === 'activate' ? 'activate' : 'deactivate';
    const confirmMessage = `Are you sure you want to ${actionText} this user and ALL users in their hierarchy? This will affect all users created under this user.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setTreeStatusLoading(true);
      const response = await userAPI.toggleUserTreeStatus(userId, action);
      if (response.data.success) {
        toast.success(response.data.message || `Successfully ${actionText}d ${response.data.summary?.total_users_affected || 0} user(s)`);
        
        // Refresh the tree data
        const treeResponse = await userAPI.getUserTree(userId);
        if (treeResponse.data.success) {
          setTreeData(treeResponse.data.tree);
          setTreeStatistics(treeResponse.data.statistics || null);
        }
        
        // Refresh users list
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${actionText} user tree`);
    } finally {
      setTreeStatusLoading(false);
    }
  };

  // Handle tab change in details dialog
  const handleDetailsTabChange = (event, newValue) => {
    setDetailsTabValue(newValue);
    // Reset pagination when switching tabs
    if (newValue === 2) {
      setBettingPage(1);
    } else if (newValue === 3) {
      setTransactionPage(1);
    }
  };

  // TabPanel component for user details
  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-details-tabpanel-${index}`}
      aria-labelledby={`user-details-tab-${index}`}
      style={{ height: '100%', overflow: 'auto' }}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  // TreeNode - Minimalist Flat List Design
  const TreeNode = ({ node, level = 0, isLast = false }) => {
    const [expanded, setExpanded] = useState(level < 2);
    const hasChildren = node.children && node.children.length > 0;

    const typeColors = {
      owner: '#dc2626',
      super_admin: '#7c3aed',
      admin: '#2563eb',
      agent: '#ea580c',
      users: '#16a34a',
    };

    const color = typeColors[node.user_type] || '#6b7280';

    return (
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto auto auto auto',
            alignItems: 'center',
            gap: 2,
            py: 1.25,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: level % 2 === 0 ? 'transparent' : 'action.hover',
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          {/* Level Indicator + Expand */}
          <Box sx={{ display: 'flex', alignItems: 'center', width: 80 }}>
            {[...Array(level)].map((_, i) => (
              <Box key={i} sx={{ width: 16, height: 16 }} />
            ))}
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{ p: 0.25 }}
              >
                {expanded ? (
                  <ExpandMoreIcon sx={{ fontSize: 20, color }} />
                ) : (
                  <ChevronRightIcon sx={{ fontSize: 20, color }} />
                )}
              </IconButton>
            ) : (
              <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
              </Box>
            )}
          </Box>

          {/* Username + Type */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {node.username}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: `${color}15`,
                color: color,
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: 10,
              }}
            >
              {node.user_type === 'super_admin' ? 'S.Admin' : node.user_type}
            </Typography>
          </Box>

          {/* Status */}
          <Box sx={{ width: 70 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: node.status === 'active' ? 'success.lighter' : 'error.lighter',
                color: node.status === 'active' ? 'success.dark' : 'error.dark',
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: node.status === 'active' ? 'success.main' : 'error.main',
                }}
              />
              <Typography variant="caption" fontWeight={500} textTransform="capitalize">
                {node.status}
              </Typography>
            </Box>
          </Box>

          {/* Balance */}
          <Typography variant="body2" fontWeight={500} sx={{ width: 100, textAlign: 'right' }}>
            ₹{(node.wallet_balance || 0).toLocaleString('en-IN')}
          </Typography>

          {/* Children Count */}
          <Typography variant="caption" color="text.secondary" sx={{ width: 50, textAlign: 'center' }}>
            {hasChildren ? `${node.total_descendants || node.children.length}` : '—'}
          </Typography>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={() => {
                  setOpenTreeDialog(false);
                  handleViewDetails(node.id);
                }}
              >
                <Visibility sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {['owner', 'super_admin', 'admin', 'agent'].includes(user.user_type) && (
              <Tooltip title="Update Wallet">
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenTreeDialog(false);
                    const treeUser = users.find(u => u.id === node.id);
                    if (treeUser) handleOpenWalletDialog(treeUser);
                    else handleViewDetails(node.id);
                  }}
                >
                  <EditIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Toggle Tree Status - Only show if user has children and permission */}
            {hasChildren && ['owner', 'super_admin', 'admin'].includes(user.user_type) && node.id !== user.id && (
              <Tooltip title={node.status === 'active' ? 'Deactivate Tree' : 'Activate Tree'}>
                <IconButton
                  size="small"
                  onClick={() => handleToggleTreeStatus(node.id, node.status === 'active' ? 'deactivate' : 'activate')}
                  disabled={treeStatusLoading}
                >
                  {node.status === 'active' ? (
                    <BlockIcon sx={{ fontSize: 18, color: 'error.main' }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Children */}
        {hasChildren && expanded && node.children.map((child, idx) => (
          <TreeNode
            key={child.id}
            node={child}
            level={level + 1}
            isLast={idx === node.children.length - 1}
          />
        ))}
      </>
    );
  };

  // Handle user status toggle
  const handleToggleUserStatus = async (userId, username, currentStatus) => {
    const action = currentStatus === 'active' ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} user "${username}"?`)) {
      try {
        const response = await userAPI.toggleUserStatus(userId);
        if (response.data.success) {
          toast.success(response.data.message);
          fetchUsers();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to ${action} user`);
      }
    }
  };

  // Handle wallet balance update
  const handleWalletUpdate = async () => {
    try {
      // Check if non-owner/super_admin user is trying to add money
      if (
        (user?.user_type !== 'owner' && user?.user_type !== 'super_admin') &&
        walletFormData.operation === 'add'
      ) {
        toast.error('Only Owner and Super Admin can add money to user wallets');
        return;
      }

      const response = await walletAPI.updateWalletBalance(selectedUser.id, walletFormData);
      if (response.data.success) {
        toast.success(response.data.message);
        setOpenWalletDialog(false);
        resetWalletForm();
        fetchUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update wallet balance');
    }
  };

  // Handle opening wallet update dialog
  const handleOpenWalletDialog = (user) => {
    setSelectedUser(user);
    // For non-owner/super_admin users, default to deduct operation
    if (user.user_type !== 'owner' && user.user_type !== 'super_admin') {
      setWalletFormData({
        amount: '',
        operation: 'deduct',
        notes: '',
      });
    } else {
      setWalletFormData({
        amount: '',
        operation: 'add',
        notes: '',
      });
    }
    setOpenWalletDialog(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      new_user_type: createUserType,
      wallet_limit: '',
      initial_balance: 0,
    });
  };

  const resetWalletForm = () => {
    setWalletFormData({
      amount: '',
      operation: 'add',
      notes: '',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      owner: 'error',
      admin: 'primary',
      agent: 'warning',
      users: 'success',
    };
    return colors[userType] || 'default';
  };

  const columns = [
    // { 
    //   field: 'id', 
    //   headerName: 'ID', 
    //   width: 70,
    //   align: 'center',
    //   headerAlign: 'center',
    // },
    { 
      field: 'username', 
      headerName: 'Username', 
      width: 200, 
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {/* <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
            {params.value.charAt(0).toUpperCase()}
          </Avatar> */}
          <Typography variant="body2">{params.value}</Typography>
          <Tooltip title="Chat on WhatsApp">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const phoneNumber = params.value.replace(/\D/g, '');
                window.open(`https://wa.me/${phoneNumber}`, '_blank');
              }}
              sx={{
                color: '#25D366',
                '&:hover': {
                  bgcolor: 'rgba(37, 211, 102, 0.1)',
                }
              }}
            >
              <WhatsAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'user_type',
      headerName: 'Type',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" width="100%">
          <Chip
            label={params.value === 'users' ? 'PLAYER' : params.value.toUpperCase()}
            color={getUserTypeColor(params.value)}
            variant="outlined"
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" justifyContent="center" width="100%">
          <Chip
            label={params.value.toUpperCase()}
            color={params.value === 'active' ? 'success' : 'error'}
            variant="filled"
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'wallet',
      headerName: 'Wallet Balance',
      width: 150,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Typography variant="body2" fontWeight="500">
            {params.row.wallet ? formatCurrency(params.row.wallet.balance) : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 240,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5} justifyContent="center" width="100%">
          <Tooltip title="View Details">
            <IconButton 
              size="small" 
              onClick={() => handleViewDetails(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {['owner', 'super_admin', 'admin', 'agent'].includes(user.user_type) && params.row.wallet && (
            <Tooltip title="Update Wallet">
              <IconButton 
                size="small" 
                color="warning"
                onClick={() => handleOpenWalletDialog(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="View User Tree">
            <IconButton 
              size="small" 
              color="primary"
              onClick={() => handleViewTree(params.row.id)}
            >
              <AccountTreeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === 'active' ? 'Deactivate User' : 'Activate User'}>
            <IconButton 
              size="small" 
              color={params.row.status === 'active' ? 'error' : 'success'}
              onClick={() => handleToggleUserStatus(params.row.id, params.row.username, params.row.status)}
            >
              <BlockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your {user.user_type === 'admin' ? 'agents and players' : 'users'}
          </Typography>
        </Box>
        
        {/* {user.user_type !== 'agent' && (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateUserType('agent');
                setFormData({ ...formData, new_user_type: 'agent' });
                setOpenCreateDialog(true);
              }}
            >
              Add Agent
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => {
                setCreateUserType('users');
                setFormData({ ...formData, new_user_type: 'users' });
                setOpenCreateDialog(true);
              }}
            >
              Add Player
            </Button>
          </Box>
        )} */}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <GroupIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="600">
                {users.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <PersonIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="600" color="success.main">
                {users.filter(u => u.status === 'active').length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <BlockIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="600" color="error.main">
                {users.filter(u => u.status === 'inactive').length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Inactive Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <AccountBalanceWallet color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="600" color="warning.main">
                {formatCurrency(users.reduce((sum, u) => sum + (u.wallet?.balance || 0), 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Balance
              </Typography>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      {/* Users Table */}
      <Card>
        <CardContent>
          {/* Filter Bar */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body2" color="text.secondary" fontWeight="500">
              Filter by Type:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label="All"
                onClick={() => setTypeFilter('all')}
                color={typeFilter === 'all' ? 'primary' : 'default'}
                variant={typeFilter === 'all' ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip
                label="Super Admin"
                onClick={() => setTypeFilter('super_admin')}
                color={typeFilter === 'super_admin' ? 'secondary' : 'default'}
                variant={typeFilter === 'super_admin' ? 'filled' : 'outlined'}
                size="small"
                sx={typeFilter === 'super_admin' ? {} : { borderColor: '#8e24aa', color: '#8e24aa' }}
              />
              <Chip
                label="Admin"
                onClick={() => setTypeFilter('admin')}
                color={typeFilter === 'admin' ? 'primary' : 'default'}
                variant={typeFilter === 'admin' ? 'filled' : 'outlined'}
                size="small"
              />
              <Chip
                label="Agent"
                onClick={() => setTypeFilter('agent')}
                color={typeFilter === 'agent' ? 'warning' : 'default'}
                variant={typeFilter === 'agent' ? 'filled' : 'outlined'}
                size="small"
                sx={typeFilter === 'agent' ? {} : { borderColor: '#fb8c00', color: '#fb8c00' }}
              />
              <Chip
                label="Player"
                onClick={() => setTypeFilter('users')}
                color={typeFilter === 'users' ? 'success' : 'default'}
                variant={typeFilter === 'users' ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>
            {typeFilter !== 'all' && (
              <Typography variant="caption" color="text.secondary">
                Showing {users.filter(u => u.user_type === typeFilter).length} of {users.length} users
              </Typography>
            )}
          </Box>

          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={typeFilter === 'all' ? users : users.filter(u => u.user_type === typeFilter)}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderColor: 'rgba(224, 224, 224, 1)',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderBottom: '2px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeader': {
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus': {
                  outline: 'none',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create New {createUserType === 'users' ? 'Player' : 'Agent'}
          <IconButton
            aria-label="close"
            onClick={() => setOpenCreateDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Username"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
            
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            
            <TextField
              label="Wallet Limit"
              type="number"
              fullWidth
              value={formData.wallet_limit}
              onChange={(e) => setFormData({ ...formData, wallet_limit: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              helperText="Leave empty for no limit"
            />
            
            <TextField
              label="Initial Balance"
              type="number"
              fullWidth
              value={formData.initial_balance}
              onChange={(e) => setFormData({ ...formData, initial_balance: parseFloat(e.target.value) || 0 })}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateUser}
            disabled={!formData.username || !formData.password}
          >
            Create {createUserType === 'users' ? 'Player' : 'Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => setOpenDetailsDialog(false)} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh', 
            maxHeight: '900px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
          {selectedUser && (
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 48, height: 48 }}>
                {selectedUser.username.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6">{selectedUser.username}</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip 
                    label={selectedUser.user_type === 'users' ? 'PLAYER' : selectedUser.user_type.toUpperCase()} 
                    color={getUserTypeColor(selectedUser.user_type)}
                    size="small"
                  />
                  <Chip 
                    label={selectedUser.status.toUpperCase()} 
                    color={selectedUser.status === 'active' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          )}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDetailsDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, flexShrink: 0 }}>
          <Tabs value={detailsTabValue} onChange={handleDetailsTabChange} aria-label="user details tabs">
            <Tab icon={<InfoIcon />} label="Basic Info" iconPosition="start" />
            <Tab icon={<TrendingUpIcon />} label="Betting Stats" iconPosition="start" />
            <Tab icon={<CasinoIcon />} label="Betting History" iconPosition="start" />
            <Tab icon={<ReceiptIcon />} label="Transactions" iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent 
          sx={{ 
            p: 0, 
            overflow: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.3)',
              },
            },
          }}
        >
          {selectedUser && (
            <>
              {/* Tab 1: Basic Information */}
              <TabPanel value={detailsTabValue} index={0}>
                <Grid container spacing={2}>
                  {/* User Information */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <PersonIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="600">User Information</Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Stack spacing={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">User ID</Typography>
                          <Typography variant="body2" fontWeight="600">#{selectedUser.id}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Username</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedUser.username}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">User Type</Typography>
                          <Chip 
                            label={selectedUser.user_type === 'users' ? 'PLAYER' : selectedUser.user_type.toUpperCase()} 
                            color={getUserTypeColor(selectedUser.user_type)}
                            size="small"
                          />
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Status</Typography>
                          <Chip 
                            label={selectedUser.status.toUpperCase()} 
                            color={selectedUser.status === 'active' ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Member Since</Typography>
                          <Typography variant="body2" fontWeight="600">
                            {new Date(selectedUser.created_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric' 
                            })}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                          <Typography variant="body2" fontWeight="600">
                            {new Date(selectedUser.updated_at).toLocaleDateString('en-US', { 
                              year: 'numeric', month: 'short', day: 'numeric' 
                            })}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                  
                  {/* Wallet Information */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <AccountBalanceWallet color="success" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="600">Wallet Information</Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      
                      {selectedUser.wallet ? (
                        <Stack spacing={1.5}>
                          <Box textAlign="center" py={2} bgcolor="rgba(76, 175, 80, 0.08)" borderRadius={2}>
                            <Typography variant="h4" color="success.main" fontWeight="700">
                              {formatCurrency(selectedUser.wallet.balance)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">Current Balance</Typography>
                          </Box>
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Wallet Limit</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {selectedUser.wallet.max_limit 
                                ? formatCurrency(selectedUser.wallet.max_limit) 
                                : 'Unlimited'}
                            </Typography>
                          </Box>
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Available to Spend</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {selectedUser.wallet.max_limit 
                                ? formatCurrency(Math.min(selectedUser.wallet.balance, selectedUser.wallet.max_limit))
                                : formatCurrency(selectedUser.wallet.balance)}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Box textAlign="center" py={4}>
                          <AccountBalanceWallet sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body1" color="text.secondary" fontWeight="500">No Wallet Available</Typography>
                          <Typography variant="caption" color="text.secondary">This user doesn't have a wallet configured.</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Quick Stats */}
                  {selectedUser.transaction_history && (
                    <Grid item xs={12}>
                      <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <TrendingUpIcon color="info" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600">Transaction Overview</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(76, 175, 80, 0.1)" borderRadius={2}>
                              <Typography variant="h5" fontWeight="700" color="success.main">
                                {formatCurrency(selectedUser.transaction_history.statistics?.total_deposits || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Total Deposits</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(244, 67, 54, 0.1)" borderRadius={2}>
                              <Typography variant="h5" fontWeight="700" color="error.main">
                                {formatCurrency(selectedUser.transaction_history.statistics?.total_withdrawals || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Total Withdrawals</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(33, 150, 243, 0.1)" borderRadius={2}>
                              <Typography variant="h5" fontWeight="700" color="info.main">
                                {formatCurrency(selectedUser.transaction_history.statistics?.total_transfers || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Total Transfers</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(255, 152, 0, 0.1)" borderRadius={2}>
                              <Typography variant="h5" fontWeight="700" color="warning.main">
                                {selectedUser.transaction_history.statistics?.pending_withdrawal_count || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">Pending Withdrawals</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* Tab 2: Betting Statistics */}
              <TabPanel value={detailsTabValue} index={1}>
                {selectedUser.betting_history && !selectedUser.betting_history.error ? (
                  <Grid container spacing={2}>
                    {/* Overall Summary Stats */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <TrendingUpIcon sx={{ color: '#9c27b0' }} fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600">Overall Summary</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={1.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bets</Typography>
                            <Typography variant="h6" fontWeight="700">
                              {selectedUser.betting_history.detailed_statistics?.combined_summary?.total_bets || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Wins</Typography>
                            <Typography variant="h6" fontWeight="700" color="success.main">
                              {selectedUser.betting_history.detailed_statistics?.combined_summary?.total_wins || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                            <Chip 
                              label={`${selectedUser.betting_history.detailed_statistics?.combined_summary?.overall_win_percentage || 0}%`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Divider />
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bet</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.combined_summary?.total_bet_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Win</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.combined_summary?.total_win_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5} bgcolor={selectedUser.betting_history.detailed_statistics?.combined_summary?.overall_net_profit_loss >= 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'} borderRadius={1}>
                            <Typography variant="body2" fontWeight="600">Net P/L</Typography>
                            <Typography 
                              variant="h6" 
                              fontWeight="700" 
                              color={selectedUser.betting_history.detailed_statistics?.combined_summary?.overall_net_profit_loss >= 0 ? 'success.main' : 'error.main'}
                            >
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.combined_summary?.overall_net_profit_loss || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Gali Game Stats */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'primary.main', borderWidth: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <CasinoIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600" color="primary">Gali Games</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={1.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bets</Typography>
                            <Typography variant="h6" fontWeight="700">
                              {selectedUser.betting_history.detailed_statistics?.gali?.total_bets || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Wins / Losses</Typography>
                            <Box display="flex" gap={1}>
                              <Chip label={`W: ${selectedUser.betting_history.detailed_statistics?.gali?.status_breakdown?.win || 0}`} size="small" color="success" />
                              <Chip label={`L: ${selectedUser.betting_history.detailed_statistics?.gali?.status_breakdown?.loss || 0}`} size="small" color="error" />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                            <Chip 
                              label={`${selectedUser.betting_history.detailed_statistics?.gali?.win_percentage || 0}%`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Divider />
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bet</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.gali?.total_bet_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Win</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.gali?.total_win_amount || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Matka Game Stats */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'warning.main', borderWidth: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <CasinoIcon sx={{ color: '#ff9800' }} fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#ff9800' }}>Matka Games</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={1.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bets</Typography>
                            <Typography variant="h6" fontWeight="700">
                              {selectedUser.betting_history.detailed_statistics?.matka?.total_bets || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Wins / Losses</Typography>
                            <Box display="flex" gap={1}>
                              <Chip label={`W: ${selectedUser.betting_history.detailed_statistics?.matka?.status_breakdown?.win || 0}`} size="small" color="success" />
                              <Chip label={`L: ${selectedUser.betting_history.detailed_statistics?.matka?.status_breakdown?.loss || 0}`} size="small" color="error" />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                            <Chip 
                              label={`${selectedUser.betting_history.detailed_statistics?.matka?.win_percentage || 0}%`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Divider />
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bet</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.matka?.total_bet_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Win</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.matka?.total_win_amount || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Aviator Game Stats */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2, border: '1px solid', borderColor: '#ff5722', borderWidth: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <FlightTakeoffIcon sx={{ color: '#ff5722' }} fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#ff5722' }}>Aviator</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={1.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bets</Typography>
                            <Typography variant="h6" fontWeight="700">
                              {selectedUser.betting_history.detailed_statistics?.aviator?.total_bets || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Cashed / Lost</Typography>
                            <Box display="flex" gap={1}>
                              <Chip label={`C: ${selectedUser.betting_history.detailed_statistics?.aviator?.status_breakdown?.cashed || 0}`} size="small" color="success" />
                              <Chip label={`L: ${selectedUser.betting_history.detailed_statistics?.aviator?.status_breakdown?.lost || 0}`} size="small" color="error" />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                            <Chip 
                              label={`${selectedUser.betting_history.detailed_statistics?.aviator?.win_percentage || 0}%`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Divider />
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bet</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.aviator?.total_bet_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Payout</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.aviator?.total_payout || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Chicken Road Game Stats */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2, border: '1px solid', borderColor: '#4caf50', borderWidth: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <EmojiNatureIcon sx={{ color: '#4caf50' }} fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#4caf50' }}>Chicken Road</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={1.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
                            <Typography variant="h6" fontWeight="700">
                              {selectedUser.betting_history.detailed_statistics?.chicken_road?.total_sessions || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Won / Lost</Typography>
                            <Box display="flex" gap={1}>
                              <Chip label={`W: ${selectedUser.betting_history.detailed_statistics?.chicken_road?.status_breakdown?.won || 0}`} size="small" color="success" />
                              <Chip label={`L: ${selectedUser.betting_history.detailed_statistics?.chicken_road?.status_breakdown?.lost || 0}`} size="small" color="error" />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                            <Chip 
                              label={`${selectedUser.betting_history.detailed_statistics?.chicken_road?.win_percentage || 0}%`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Divider />
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bet</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.chicken_road?.total_bet_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Payout</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.chicken_road?.total_payout || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* 7 Up Down Game Stats */}
                    <Grid item xs={12} md={4}>
                      <Paper elevation={1} sx={{ p: 2.5, height: '100%', borderRadius: 2, border: '1px solid', borderColor: '#2196f3', borderWidth: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Filter7Icon sx={{ color: '#2196f3' }} fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#2196f3' }}>7 Up Down</Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Stack spacing={1.5}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bets</Typography>
                            <Typography variant="h6" fontWeight="700">
                              {selectedUser.betting_history.detailed_statistics?.seven_up_down?.total_bets || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Won / Lost</Typography>
                            <Box display="flex" gap={1}>
                              <Chip label={`W: ${selectedUser.betting_history.detailed_statistics?.seven_up_down?.status_breakdown?.won || 0}`} size="small" color="success" />
                              <Chip label={`L: ${selectedUser.betting_history.detailed_statistics?.seven_up_down?.status_breakdown?.lost || 0}`} size="small" color="error" />
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Win Rate</Typography>
                            <Chip 
                              label={`${selectedUser.betting_history.detailed_statistics?.seven_up_down?.win_percentage || 0}%`}
                              color="info"
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                          </Box>
                          <Divider />
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Bet</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.seven_up_down?.total_bet_amount || 0)}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Total Payout</Typography>
                            <Typography variant="body2" fontWeight="600" color="success.main">
                              {formatCurrency(selectedUser.betting_history.detailed_statistics?.seven_up_down?.total_payout || 0)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    {/* Status Breakdown */}
                    <Grid item xs={12}>
                      <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="600" mb={2}>Bet Status Overview</Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(33, 150, 243, 0.1)" borderRadius={2} border="1px solid" borderColor="info.main">
                              <Typography variant="h4" fontWeight="700" color="info.main">
                                {selectedUser.betting_history.detailed_statistics?.combined_summary?.total_pending || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight="500">Pending</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(76, 175, 80, 0.1)" borderRadius={2} border="1px solid" borderColor="success.main">
                              <Typography variant="h4" fontWeight="700" color="success.main">
                                {selectedUser.betting_history.detailed_statistics?.combined_summary?.total_wins || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight="500">Wins</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(244, 67, 54, 0.1)" borderRadius={2} border="1px solid" borderColor="error.main">
                              <Typography variant="h4" fontWeight="700" color="error.main">
                                {selectedUser.betting_history.detailed_statistics?.combined_summary?.total_losses || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight="500">Losses</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box textAlign="center" p={2} bgcolor="rgba(255, 152, 0, 0.1)" borderRadius={2} border="1px solid" borderColor="warning.main">
                              <Typography variant="h4" fontWeight="700" color="warning.main">
                                {selectedUser.betting_history.detailed_statistics?.combined_summary?.total_cancelled || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight="500">Cancelled</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                ) : (
                  <Box textAlign="center" py={8}>
                    <CasinoIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight="500">No Betting Data Available</Typography>
                    <Typography variant="body2" color="text.secondary">This user hasn't placed any bets yet.</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 3: Betting History */}
              <TabPanel value={detailsTabValue} index={2}>
                {selectedUser.betting_history && (
                  selectedUser.betting_history.recent_gali_bets?.length > 0 || 
                  selectedUser.betting_history.recent_matka_bets?.length > 0 ||
                  selectedUser.betting_history.recent_aviator_bets?.length > 0 ||
                  selectedUser.betting_history.recent_chicken_road_sessions?.length > 0 ||
                  selectedUser.betting_history.recent_seven_up_down_bets?.length > 0
                ) ? (
                  (() => {
                    // Combine all bets and add type identifier
                    const galiBets = (selectedUser.betting_history.recent_gali_bets || []).map(bet => ({
                      ...bet,
                      gameType: 'gali',
                      date: bet.placed_at
                    }));
                    
                    const matkaBets = (selectedUser.betting_history.recent_matka_bets || []).map(bet => ({
                      ...bet,
                      gameType: 'matka',
                      date: bet.createdAt
                    }));

                    const aviatorBets = (selectedUser.betting_history.recent_aviator_bets || []).map(bet => ({
                      ...bet,
                      gameType: 'aviator',
                      date: bet.placed_at
                    }));

                    const chickenRoadBets = (selectedUser.betting_history.recent_chicken_road_sessions || []).map(bet => ({
                      ...bet,
                      gameType: 'chicken_road',
                      date: bet.started_at
                    }));

                    const sevenUpDownBets = (selectedUser.betting_history.recent_seven_up_down_bets || []).map(bet => ({
                      ...bet,
                      gameType: 'seven_up_down',
                      date: bet.placed_at
                    }));
                    
                    // Combine and sort by date (most recent first)
                    const allBets = [...galiBets, ...matkaBets, ...aviatorBets, ...chickenRoadBets, ...sevenUpDownBets].sort((a, b) => 
                      new Date(b.date) - new Date(a.date)
                    );

                    // Helper function to get game type display
                    const getGameTypeLabel = (gameType) => {
                      const labels = {
                        gali: 'GALI',
                        matka: 'MATKA',
                        aviator: 'AVIATOR',
                        chicken_road: 'CHICKEN',
                        seven_up_down: '7 UP DOWN'
                      };
                      return labels[gameType] || gameType.toUpperCase();
                    };

                    const getGameTypeColor = (gameType) => {
                      const colors = {
                        gali: 'primary',
                        matka: 'warning',
                        aviator: 'error',
                        chicken_road: 'success',
                        seven_up_down: 'info'
                      };
                      return colors[gameType] || 'default';
                    };

                    const getStatusColor = (status) => {
                      const lowerStatus = status?.toLowerCase();
                      if (['win', 'won', 'cashed'].includes(lowerStatus)) return 'success';
                      if (['loss', 'lost'].includes(lowerStatus)) return 'error';
                      if (['pending', 'open', 'active'].includes(lowerStatus)) return 'warning';
                      return 'default';
                    };

                    const isWinStatus = (status) => {
                      const lowerStatus = status?.toLowerCase();
                      return ['win', 'won', 'cashed'].includes(lowerStatus);
                    };
                    
                    // Pagination
                    const startIndex = (bettingPage - 1) * bettingPageSize;
                    const endIndex = startIndex + bettingPageSize;
                    const paginatedBets = allBets.slice(startIndex, endIndex);
                    const totalPages = Math.ceil(allBets.length / bettingPageSize);
                    
                    return (
                      <Stack spacing={2}>
                        <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <Box p={2} bgcolor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" display="flex" alignItems="center" justifyContent="space-between" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CasinoIcon fontSize="small" />
                              <Typography variant="subtitle1" fontWeight="600">
                                All Betting History ({allBets.length} Total Bets)
                              </Typography>
                            </Box>
                            <Typography variant="body2">
                              Page {bettingPage} of {totalPages}
                            </Typography>
                          </Box>
                          
                          <TableContainer sx={{ maxHeight: 500 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Type</TableCell>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Game/Details</TableCell>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Market/Info</TableCell>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Number/Option</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Bet Amount</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Win/Payout</TableCell>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Status</TableCell>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedBets.length > 0 ? (
                                  paginatedBets.map((bet, index) => (
                                    <TableRow key={`${bet.gameType}-${bet.id}-${index}`} hover>
                                      <TableCell>
                                        <Chip 
                                          label={getGameTypeLabel(bet.gameType)}
                                          size="small"
                                          color={getGameTypeColor(bet.gameType)}
                                          sx={{ fontWeight: 600, minWidth: 70 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="500">
                                          {bet.gameType === 'gali' && (bet.bet_type_name || bet.game_mode || 'N/A')}
                                          {bet.gameType === 'matka' && (bet.bet_type_name || 'N/A')}
                                          {bet.gameType === 'aviator' && `Multiplier: ${bet.cashout_multiplier || bet.crash_multiplier || '-'}x`}
                                          {bet.gameType === 'chicken_road' && `Stage: ${bet.current_stage || 0}/${bet.max_stages || 10}`}
                                          {bet.gameType === 'seven_up_down' && (bet.bet_option || 'N/A')}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {bet.gameType === 'gali' && (bet.market?.market_name || 'N/A')}
                                          {bet.gameType === 'matka' && (bet.market?.market_name || 'N/A')}
                                          {bet.gameType === 'aviator' && `Round: ${bet.round_id || 'N/A'}`}
                                          {bet.gameType === 'chicken_road' && `Difficulty: ${bet.difficulty || 'N/A'}`}
                                          {bet.gameType === 'seven_up_down' && `Round: ${bet.round_id || 'N/A'}`}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {bet.gameType === 'gali' && (
                                          <Box>
                                            <Typography variant="body2" fontWeight="500">
                                              {bet.bet_category_display || 'N/A'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                              {bet.original_vs_generated ? 
                                                `${bet.original_vs_generated.original} → ${bet.original_vs_generated.generated}` : 
                                                'N/A'}
                                            </Typography>
                                          </Box>
                                        )}
                                        {bet.gameType === 'matka' && (
                                          <Typography variant="body2" fontFamily="monospace" fontWeight="500">
                                            {bet.formatted_number || bet.number || 'N/A'}
                                          </Typography>
                                        )}
                                        {bet.gameType === 'aviator' && (
                                          <Typography variant="body2" fontWeight="500">
                                            Auto: {bet.auto_cashout_multiplier ? `${bet.auto_cashout_multiplier}x` : 'OFF'}
                                          </Typography>
                                        )}
                                        {bet.gameType === 'chicken_road' && (
                                          <Typography variant="body2" fontWeight="500">
                                            Mult: {bet.current_multiplier ? `${bet.current_multiplier}x` : '-'}
                                          </Typography>
                                        )}
                                        {bet.gameType === 'seven_up_down' && (
                                          <Typography variant="body2" fontWeight="500">
                                            Result: {bet.result || '-'}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight="600">
                                          {formatCurrency(bet.display_amount || bet.amount || bet.bet_amount || 0)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" color="success.main" fontWeight="600">
                                          {isWinStatus(bet.status) ? formatCurrency(bet.win_amount || bet.payout_amount || 0) : '-'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={(bet.status || 'N/A').toUpperCase()} 
                                          size="small"
                                          color={getStatusColor(bet.status)}
                                          sx={{ minWidth: 70, fontWeight: 600 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2" fontWeight="500">
                                          {new Date(bet.date).toLocaleDateString()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {new Date(bet.date).toLocaleTimeString()}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={8} align="center">
                                      <Typography variant="body2" color="text.secondary" py={2}>
                                        No bets found
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          
                          {totalPages > 1 && (
                            <Box display="flex" justifyContent="center" alignItems="center" p={2} gap={2}>
                              <Typography variant="body2" color="text.secondary">
                                Showing {startIndex + 1} - {Math.min(endIndex, allBets.length)} of {allBets.length} bets
                              </Typography>
                              <Pagination 
                                count={totalPages} 
                                page={bettingPage} 
                                onChange={(e, page) => setBettingPage(page)}
                                color="primary"
                                showFirstButton
                                showLastButton
                                siblingCount={1}
                                boundaryCount={1}
                              />
                            </Box>
                          )}
                        </Paper>
                      </Stack>
                    );
                  })()
                ) : (
                  <Box textAlign="center" py={8}>
                    <CasinoIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight="500">No Betting History</Typography>
                    <Typography variant="body2" color="text.secondary">This user hasn't placed any bets yet.</Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Tab 4: Transaction History */}
              <TabPanel value={detailsTabValue} index={3}>
                {selectedUser.transaction_history && selectedUser.transaction_history.recent_transactions?.length > 0 ? (
                  (() => {
                    const transactions = selectedUser.transaction_history.recent_transactions;
                    const startIndex = (transactionPage - 1) * transactionPageSize;
                    const endIndex = startIndex + transactionPageSize;
                    const paginatedTransactions = transactions.slice(startIndex, endIndex);
                    const totalPages = Math.ceil(transactions.length / transactionPageSize);
                    
                    return (
                      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box p={2} bgcolor="info.main" color="white" display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={1}>
                            <ReceiptIcon fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="600">Recent Transactions ({transactions.length})</Typography>
                          </Box>
                          <Typography variant="body2">
                            Page {transactionPage} of {totalPages}
                          </Typography>
                        </Box>
                        
                        <TableContainer sx={{ maxHeight: 500 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>From</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>To</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Date & Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.default' }}>Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedTransactions.map((transaction) => (
                                <TableRow key={transaction.id} hover>
                                  <TableCell>
                                    <Chip 
                                      label={transaction.transaction_type.toUpperCase()}
                                      size="small"
                                      color={transaction.transaction_type === 'deposit' ? 'success' : 
                                            transaction.transaction_type === 'withdrawal' ? 'error' : 'info'}
                                      sx={{ fontWeight: 600, minWidth: 90 }}
                                    />
                                  </TableCell>
                                  <TableCell><Typography variant="body2">{transaction.fromUser?.username || 'N/A'}</Typography></TableCell>
                                  <TableCell><Typography variant="body2">{transaction.toUser?.username || 'N/A'}</Typography></TableCell>
                                  <TableCell align="right">
                                    <Typography 
                                      variant="body2"
                                      fontWeight="700"
                                      color={transaction.transaction_type === 'deposit' ? 'success.main' : 
                                            transaction.transaction_type === 'withdrawal' ? 'error.main' : 'info.main'}
                                    >
                                      {transaction.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={transaction.status.toUpperCase()} 
                                      size="small"
                                      color={transaction.status === 'success' ? 'success' : 
                                            transaction.status === 'pending' ? 'warning' : 'error'}
                                      variant={transaction.status === 'pending' ? 'outlined' : 'filled'}
                                      sx={{ minWidth: 80, fontWeight: 600 }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="500">
                                      {new Date(transaction.created_at).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(transaction.created_at).toLocaleTimeString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, display: 'block' }}>
                                      {transaction.notes || '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        {totalPages > 1 && (
                          <Box display="flex" justifyContent="center" p={2}>
                            <Pagination 
                              count={totalPages} 
                              page={transactionPage} 
                              onChange={(e, page) => setTransactionPage(page)}
                              color="primary"
                              showFirstButton
                              showLastButton
                            />
                          </Box>
                        )}
                      </Paper>
                    );
                  })()
                ) : (
                  <Box textAlign="center" py={8}>
                    <ReceiptIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" fontWeight="500">No Transaction History</Typography>
                    <Typography variant="body2" color="text.secondary">This user hasn't made any transactions yet.</Typography>
                  </Box>
                )}
              </TabPanel>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Wallet Update Dialog */}
      <Dialog open={openWalletDialog} onClose={() => setOpenWalletDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Wallet Balance
          {selectedUser && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              User: {selectedUser.username} | Current Balance: {selectedUser.wallet ? formatCurrency(selectedUser.wallet.balance) : 'N/A'}
            </Typography>
          )}
          <IconButton
            aria-label="close"
            onClick={() => setOpenWalletDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {(user?.user_type === 'owner' || user?.user_type === 'super_admin') && (
              <Alert severity="info">
                You can both add and deduct money for this user.
              </Alert>
            )}
            {user?.user_type !== 'owner' && user?.user_type !== 'super_admin' && (
              <Alert severity="warning">
                You can only deduct money from this user's wallet.
              </Alert>
            )}
            
            <FormControl fullWidth>
              <InputLabel>Operation</InputLabel>
              <Select
                value={walletFormData.operation}
                label="Operation"
                onChange={(e) => setWalletFormData({ ...walletFormData, operation: e.target.value })}
              >
                {(user?.user_type === 'owner' || user?.user_type === 'super_admin') && (
                  <MenuItem value="add">Add Money</MenuItem>
                )}
                <MenuItem value="deduct">Deduct Money</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={walletFormData.amount}
              onChange={(e) => setWalletFormData({ ...walletFormData, amount: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              required
              helperText={`Current Balance: ${selectedUser?.wallet ? formatCurrency(selectedUser.wallet.balance) : 'N/A'}`}
            />
            
            <TextField
              label="Notes (Optional)"
              multiline
              rows={3}
              fullWidth
              value={walletFormData.notes}
              onChange={(e) => setWalletFormData({ ...walletFormData, notes: e.target.value })}
              placeholder="Enter reason for wallet update..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWalletDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleWalletUpdate}
            disabled={!walletFormData.amount || parseFloat(walletFormData.amount) <= 0}
            color={walletFormData.operation === 'add' ? 'success' : 'warning'}
          >
            {walletFormData.operation === 'add' ? 'Add Money' : 'Deduct Money'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Tree Dialog */}
      <Dialog 
        open={openTreeDialog} 
        onClose={() => {
          setOpenTreeDialog(false);
          setTreeData(null);
          setTreeStatistics(null);
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            height: '85vh', 
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
          }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2.5, pb: 2, flexShrink: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AccountTreeIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  User Hierarchy
                </Typography>
                {treeData && (
                  <Typography variant="body2" color="text.secondary">
                    Starting from <strong>{treeData.username}</strong>
                  </Typography>
                )}
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Master Tree Toggle Button */}
              {treeData && !treeLoading && ['owner', 'super_admin', 'admin'].includes(user.user_type) && treeData.id !== user.id && (
                <Tooltip title={treeData.status === 'active' ? 'Deactivate Entire Tree' : 'Activate Entire Tree'}>
                  <Button
                    variant="outlined"
                    size="small"
                    color={treeData.status === 'active' ? 'error' : 'success'}
                    startIcon={treeStatusLoading ? <CircularProgress size={16} /> : (treeData.status === 'active' ? <BlockIcon /> : <CheckCircleIcon />)}
                    onClick={() => handleToggleTreeStatus(treeData.id, treeData.status === 'active' ? 'deactivate' : 'activate')}
                    disabled={treeStatusLoading}
                    sx={{ mr: 1 }}
                  >
                    {treeData.status === 'active' ? 'Deactivate All' : 'Activate All'}
                  </Button>
                </Tooltip>
              )}
              <IconButton
                onClick={() => {
                  setOpenTreeDialog(false);
                  setTreeData(null);
                  setTreeStatistics(null);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Statistics Bar */}
          {treeStatistics && !treeLoading && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'space-around',
              }}
            >
              <Box textAlign="center">
                <Typography variant="h5" fontWeight="700" color="primary.main">
                  {treeStatistics.total_users}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Users
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight="700" color="success.main">
                  {treeStatistics.by_status?.active || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight="700" color="warning.main">
                  ₹{(treeStatistics.total_wallet_balance || 0).toLocaleString('en-IN')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Balance
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight="700" color="info.main">
                  {treeStatistics.max_depth || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Levels Deep
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Tree Content */}
        <DialogContent sx={{ p: 2.5, overflow: 'auto', flex: 1 }}>
          {treeLoading ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
              <CircularProgress size={40} />
              <Typography color="text.secondary">Loading hierarchy...</Typography>
            </Box>
          ) : treeData ? (
            <Box>
              <TreeNode node={treeData} />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={1}>
              <AccountTreeIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography color="text.secondary">No hierarchy data available</Typography>
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenTreeDialog(false);
              setTreeData(null);
              setTreeStatistics(null);
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    </motion.div>
  );
};

export default UserManagement;
